import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { API_ENDPOINTS } from "../../api/config";
import { useUser } from "../../user_context";
import EmpLayout from "./emp_layout";
import "./css/employer_dashboard.css";

const pillClass = { active: "pill-active", paused: "pill-paused", closed: "pill-closed" };

export default function EmployerDashboard() {
  const navigate = useNavigate();
  const [dashData, setDashData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [listings, setListings] = useState([]);
  const [shortList, setShortList] = useState([]);
  const { userData } = useUser();

  useEffect(() => {
    const fetchDashboardData = async () => {
      const token = localStorage.getItem("token");
      if (!token) { navigate("/signin"); return; }
      try {
        const response = await fetch(API_ENDPOINTS.EMPDASHBOARD, {
          headers: { "Authorization": `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setDashData(data);
          setListings(data.listings || []);
          const allShortlisted = (data.listings || []).flatMap(listing =>
            listing.shortlistedApplicants.map(applicant => ({
              ...applicant,
              jobTitle: listing.title
            }))
          );
          setShortList(allShortlisted);
        } else {
          navigate("/signin");
        }
      } catch (err) {
        console.error("Dashboard load error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchDashboardData();
  }, [navigate]);

  if (loading) {
    return (
      <EmpLayout title="Loading..." titleEm="Please wait ◈">
        <div className="emd-loading-container">
          <div className="si-spinner"></div>
          <div className="emd-loading-text">Synchronizing Dashboard</div>
        </div>
      </EmpLayout>
    );
  }

  return (
    <EmpLayout 
      title="Welcome back," 
      titleEm={`${dashData?.profile?.businessName || userData?.firstName} ◈`}
    >
      <div className="emd-fade">

        {/* 1. Dynamic Banner: Encouragement based on data */}
        <div className="emd-post-card">
          <div className="emd-post-card-text">
            {listings.length > 0 ? (
              <>
                <div className="emd-post-card-title">You have {dashData?.stats?.activeListings || 0} active listing(s) attracting candidates</div>
                <div className="emd-post-card-sub">Keep the momentum going by posting another specialized role.</div>
              </>
            ) : (
              <>
                <div className="emd-post-card-title">Ready to find your next star professional?</div>
                <div className="emd-post-card-sub">Your dashboard is waiting for your first listing. Post a job in under 2 minutes.</div>
              </>
            )}
          </div>
          <Link to="/employer/post-job" className="emd-post-card-btn">+ Post a New Job</Link>
        </div>

        {/* stats */}
        <div className="emd-stats-row">
          {[
            { label: "Total Applications", value: dashData?.stats?.totalApplications || 0, sub: "across all listings", trend: "Latest data", cls: "eup" },
            { label: "Active Listings", value: dashData?.stats?.activeListings || 0, sub: "roles open", trend: `${dashData?.stats?.closedListings || 0} closed`, cls: "eneutral" },
            { label: "Shortlisted", value: dashData?.stats?.totalShortlisted || 0, sub: "candidates", trend: "Top talent", cls: "eup" },
          ].map(s => (
            <div key={s.label} className="emd-stat-card">
              <div className="emd-stat-label">{s.label}</div>
              <div className="emd-stat-value">{s.value}</div>
              <div className="emd-stat-sub">{s.sub}</div>
              <div className={`emd-stat-trend ${s.cls}`}>{s.trend}</div>
            </div>
          ))}
        </div>

        <div className="emd-grid3">
          {/* 2. Listings Column with Guided Empty State */}
          <div>
            <div className="emd-section-hdr">
              <div className="emd-section-title">My <em>listings</em></div>
              {listings.length > 0 && <Link to="/employer/listings" className="emd-view-all">Manage all →</Link>}
            </div>
            <div className="emd-listing-list">
              {listings.length > 0 ? (
                listings.map(listing => (
                  <div key={listing.id} className="emd-listing-card">
                    <div className="emd-listing-top">
                      <div className="emd-listing-icon">✂️</div>
                      <div className="emd-listing-info">
                        <div className="emd-listing-title">{listing.title}</div>
                        <div className="emd-listing-meta">Posted {new Date(listing.createdAt).toLocaleDateString()}</div>
                      </div>
                      <div className="emd-listing-status">
                        <span className={`emd-listing-pill ${pillClass[listing.status]}`}>{listing.status}</span>
                      </div>
                    </div>
                    <div className="emd-listing-divider" />
                    <div className="emd-listing-bottom">
                      <div className="emd-listing-stats">
                        {[["Applicants", listing.totalApplications], ["Views", listing.viewCount]].map(([k, v]) => (
                          <div key={k} className="emd-listing-stat">
                            <div className="emd-listing-stat-n">{v}</div>
                            <div className="emd-listing-stat-l">{k}</div>
                          </div>
                        ))}
                      </div>
                      <div className="emd-listing-actions">
                        <button className="emd-listing-btn primary" onClick={() => navigate(`/employer/listing-details/${listing.id}`)}>View</button>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="emd-listing-card emd-empty-container">
                  <div className="emd-empty-content">
                    <div className="emd-empty-icon">📂</div>
                    <h3 className="emd-empty-title">No active listings</h3>
                    <p className="emd-empty-text">
                      Start reaching out to beauty professionals by posting your first job opening.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* 3. Shortlist Panel with "How-to" Empty State */}
          <div>
            <div className="emd-section-hdr">
              <div className="emd-section-title">Candidate <em>shortlist</em></div>
            </div>
            <div className="emd-panel-card">
              <div className="emd-shortlist-list">
                {shortList.length > 0 ? (
                  shortList.map((c) => (
                    <div key={c.id} className="emd-shortlist-row" onClick={() => navigate(`/employer/candidate/${c.id}`)}>
                      <div className="emd-shortlist-avatar">{c.applicantName.charAt(0)}</div>
                      <div className="emd-shortlist-info">
                        <div className="emd-shortlist-name">{c.applicantName}</div>
                        <div className="emd-shortlist-spec">{c.jobTitle}</div>
                      </div>
                      <div className="emd-shortlist-score">{c.matchScore}%</div>
                    </div>
                  ))
                ) : (
                  <div className="emd-empty-panel">
                    <div className="emd-empty-icon-small">⭐</div>
                    <div className="emd-empty-title-small">Empty Shortlist</div>
                    <p className="emd-empty-text-small">
                      Review applicants and click "Shortlist" to save the best talent here for quick access.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* 4. Recent Applications with Interactive Empty State
        <div style={{ marginTop: '2rem' }}>
          <div className="emd-section-hdr">
            <div className="emd-section-title">Recent <em>applications</em></div>
          </div>
          <div className="emd-panel-card">
            {dashData?.recentApplications && dashData.recentApplications.length > 0 ? (
              dashData.recentApplications.map((app) => (
                <div key={app.id} className="emd-app-row">
                  <div className="emd-cand-avatar">
                    {app.applicantName.split(" ").map(n => n[0]).join("").toUpperCase()}
                  </div>
                  <div className="emd-cand-info">
                    <div className="emd-cand-name">{app.applicantName}</div>
                    <div className="emd-cand-role">Applied for: {app.jobTitle}</div>
                  </div>
                  <span className="emd-cand-match">{new Date(app.appliedAt).toLocaleDateString()}</span>
                  <div className="emd-cand-actions">
                    <button className="emd-cand-btn shortlist" onClick={() => navigate(`/employer/applicants/${app.id}`)}>
                      View Profile
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="emd-no-app-state">
                <div className="emd-empty-icon">🤝</div>
                <div className="emd-empty-title">Waiting for candidates</div>
                <div className="emd-empty-text">
                  As soon as professionals apply to your jobs, their profiles and match scores will appear here.
                </div>
              </div>
            )}
          </div>
        </div> */}

      </div>
    </EmpLayout>
  );
}